import { makeAutoObservable, reaction } from "mobx";
import { isSomeEnum } from "helpers/enum";
import { type BillingControllerStore } from "services/billingController/billingControllerStore";
import { BillingItem, CompanyProductToBillingItemMapper } from "services/checkoutBasket/checkoutBasketTypes";
import { type CompanyStore } from "services/company/companyStore";
import { type CompanyAddressStore } from "services/companyAddress/gb/companyAddressStore";
import { type CompanyProductsStore } from "services/companyProducts/companyProductsStore";
import { CompanyProduct, VirtualOfficeBillingPeriod } from "services/companyProducts/companyProductsTypes";
import { type FeatureStore } from "services/feature/featureStore";
import { type FormationExpressStore } from "services/formationExpress/formationExpressStore";
import { type PricingPlanStore } from "services/pricingPlan/gb/pricingPlanStore";
import { PricingPlan } from "services/pricingPlan/pricingPlanTypes";
import { FormError } from "types/errors";

const ALLOWED_ITEMS = new Set<BillingItem>([
  BillingItem.VirtualOffice,
  BillingItem.Taxes,
  BillingItem.FormationExpress,
]);

// The backend rejects these add-ons on the introducer free plan
const INTRODUCER_FREE_EXCLUDED_ITEMS = new Set<BillingItem>([BillingItem.Taxes]);

const mapper = new CompanyProductToBillingItemMapper({
  [CompanyProduct.VirtualOffice]: BillingItem.VirtualOffice,
  [CompanyProduct.Taxes]: BillingItem.Taxes,
  [CompanyProduct.VatRegistration]: BillingItem.VatRegistration,
  [CompanyProduct.ConfirmationStatement]: BillingItem.ConfirmationStatement,
  [CompanyProduct.SharesManagement]: BillingItem.SharesManagement,
  [CompanyProduct.DirectorManagement]: BillingItem.DirectorManagement,
  [CompanyProduct.CancellationCover]: BillingItem.CancellationCover,
  [CompanyProduct.PayeRegistration]: BillingItem.PayeRegistration,
  [CompanyProduct.SameDayRegistration]: BillingItem.FormationExpress,
});

type CheckoutBasketStoreState = "default" | "virtual-office-delete";

export class CheckoutBasketStore {
  private _addedItems: BillingItem[] = [];
  private _additionalItems: BillingItem[] = [];
  private _selectedPlanProducts: BillingItem[] = [];
  private _state: CheckoutBasketStoreState = "default";
  private _combinedCheckoutMobileStep: "cart" | "payment" = "cart";

  processingItem: Nullable<BillingItem> = null;

  constructor(
    private readonly _companyStore: CompanyStore,
    private readonly _companyAddressStore: CompanyAddressStore,
    private readonly _companyProductsStore: CompanyProductsStore,
    private readonly _formationExpressStore: FormationExpressStore,
    private readonly _pricingPlanStore: PricingPlanStore,
    private readonly _billingControllerStore: BillingControllerStore,
    private readonly _featureStore: FeatureStore,
  ) {
    this._distributeItems();

    makeAutoObservable(this);

    void this._formationExpressStore.refresh();

    reaction(
      () => [this._companyStore.company?.pricingPlan, this._companyProductsStore.productsData],
      () => {
        this._distributeItems();
        this._refreshPaymentStatus();
      },
    );

    // Watch for FormationExpress feature expiration
    reaction(
      () => this._formationExpressStore.featureAvailable,
      (featureAvailable) => {
        // When feature expires, remove from backend if user has it
        if (!featureAvailable && this._companyProductsStore.hasProduct(CompanyProduct.SameDayRegistration)) {
          void this._companyProductsStore.deleteProduct(CompanyProduct.SameDayRegistration);
        }
        this._distributeItems();
      },
    );
  }

  private _refreshPaymentStatus(): void {
    if (this._featureStore.hasFeature("formationPaymentStatus")) {
      void this._billingControllerStore.fetchPaymentStatus();
    }
  }

  get state(): CheckoutBasketStoreState {
    return this._state;
  }

  private set state(value: CheckoutBasketStoreState) {
    this._state = value;
  }

  get addedItems(): BillingItem[] {
    return this._addedItems;
  }

  set addedItems(value: BillingItem[]) {
    this._addedItems = value;
  }

  get additionalItems(): BillingItem[] {
    return this._additionalItems;
  }

  set additionalItems(value: BillingItem[]) {
    this._additionalItems = value;
  }

  get selectedPlanProducts(): BillingItem[] {
    return this._selectedPlanProducts;
  }
  set selectedPlanProducts(value: BillingItem[]) {
    this._selectedPlanProducts = value;
  }

  get showFormationExpress(): boolean {
    return this._formationExpressStore.needToShow;
  }

  get isIntroducerFreeWithoutAddons(): boolean {
    return (
      this._companyStore.getCompany().pricingPlan === PricingPlan.IntroducerFreeRegistration &&
      this._addedItems.length === 0
    );
  }

  get showPrivacyWarning(): boolean {
    if (
      this._companyStore.getCompany().useHomeAddressAsRegistered &&
      this._pricingPlanStore.getPricingPlan() === PricingPlan.Privacy
    ) {
      return true;
    }
    return false; // This can be customized based on your requirements
  }

  showUsefulAddons(): boolean {
    const result = this.additionalItems.length > 0;
    if (this.additionalItems.length === 1 && this.additionalItems[0] === BillingItem.FormationExpress) {
      return false;
    }
    return result;
  }

  cancelVirtualOfficeDeleting(): void {
    this.state = "default";
    this._distributeItems();
  }

  // No client-side validation needed — basket items are managed via store actions and server validates on charge
  validateForm(): boolean {
    return true;
  }

  get combinedCheckoutMobileStep(): "cart" | "payment" {
    return this._combinedCheckoutMobileStep;
  }

  set combinedCheckoutMobileStep(value: "cart" | "payment") {
    this._combinedCheckoutMobileStep = value;
  }

  async addItem(item: BillingItem, data?: unknown): Promise<void> {
    this.processingItem = item;

    try {
      switch (item) {
        case BillingItem.VirtualOffice: {
          const isVoBillingPeriod = isSomeEnum(VirtualOfficeBillingPeriod);
          if (!isVoBillingPeriod(data)) {
            throw new FormError("Wrong billing period");
          }
          await this._companyAddressStore.updateAddress({ virtualOfficeBillingPeriod: data });
          break;
        }
        default: {
          const product = mapper.getProduct(item);
          await this._companyProductsStore.addProduct(product);
        }
      }

      this._distributeItems();
    } finally {
      this.processingItem = null;
    }
  }

  async removeItem(item: BillingItem): Promise<void> {
    if (item === BillingItem.VirtualOffice) {
      this.state = "virtual-office-delete";
      return;
    }

    this.processingItem = item;

    try {
      const product = mapper.getProduct(item);
      await this._companyProductsStore.deleteProduct(product);

      this._distributeItems();
    } finally {
      this.processingItem = null;
    }
  }

  private _distributeItems(): void {
    type ItemDistributionResult = { addedItems: BillingItem[]; additionalItems: BillingItem[] };
    const isIntroducerFree = this._companyStore.company?.pricingPlan === PricingPlan.IntroducerFreeRegistration;
    const itemDistributionFiltered = mapper.billingItems
      .filter((billingItem) => ALLOWED_ITEMS.has(billingItem))
      .filter((billingItem) => !(isIntroducerFree && INTRODUCER_FREE_EXCLUDED_ITEMS.has(billingItem)))
      .filter((billingItem) => !this._pricingPlanStore.isProductIncluded(mapper.getProduct(billingItem)));
    const itemDistribution = itemDistributionFiltered.reduce<ItemDistributionResult>(
      (result, billingItem) => {
        switch (billingItem) {
          case BillingItem.VirtualOffice:
            if (this._companyProductsStore.hasProduct(CompanyProduct.VirtualOffice)) {
              result.addedItems.push(billingItem);
            } else {
              result.additionalItems.push(billingItem);
            }
            break;
          case BillingItem.FormationExpress: {
            const { featureAvailable } = this._formationExpressStore;
            const hasProduct = this._companyProductsStore.hasProduct(mapper.getProduct(billingItem));

            // Only show FormationExpress if featureAvailable is true
            if (!featureAvailable) {
              break;
            }

            if (hasProduct) {
              result.addedItems.push(billingItem);
            } else {
              result.additionalItems.push(billingItem);
            }
            break;
          }
          default: {
            if (this._companyProductsStore.hasProduct(mapper.getProduct(billingItem))) {
              result.addedItems.push(billingItem);
            } else {
              result.additionalItems.push(billingItem);
            }
          }
        }

        return result;
      },
      { addedItems: [], additionalItems: [] },
    );

    const excludedPricingPlanProducts = mapper.billingItems
      .filter((billingItem) => this._pricingPlanStore.isProductIncluded(mapper.getProduct(billingItem)))
      .map((billingItem) => mapper.getProduct(billingItem));
    const selectedProducts = this._companyProductsStore.productsData.map((product) => product.product);

    const selectedPlanProducts = excludedPricingPlanProducts
      .filter((product) => selectedProducts.includes(product))
      .map((product) => mapper.getBillingName(product));

    this.selectedPlanProducts = selectedPlanProducts;
    this.addedItems = itemDistribution.addedItems;
    this.additionalItems = itemDistribution.additionalItems;
  }
}
